import axios from 'axios'

import { getRequestConfig } from '.'
import { PeriodPickerValue } from '../components/common/PeriodPicker/PeriodPicker'
import {
  AccountsUpdateReqBody,
  Bank,
  BankingConsentReqBody,
  BankingUrlRequest,
  BankTransaction,
  MatchInvoiceReqBody,
  SelectionInterval,
  TransactionsFilterPeriodRequest,
  TransactionViewOptionRequest,
  UnMatchInvoiceReqBody,
} from '../Interfaces/Banking'

interface BanksByCountryResponse {
  companyId: string
  banks: Bank[]
}

interface BankingUrlResponse {
  companyId: string
  bankId: string
  bankingUrl: string
}

interface BankingConsentResponse {
  companyId: string
  status: 'OK'
}

interface RemoveBankingConsentResponse {
  companyId: string
  status: 'OK'
}

interface BanksWithAccountsResponse {
  banks: Bank[]
  companyId: string
}

interface BankTransactionsResponse {
  companyId: string
  dateStart: string
  dateEnd: string
  transactions: BankTransaction[]
  settings?: {
    transactionsFilterPeriod?: SelectionInterval
  }
}

interface MatchInvoicesResponse {
  companyId: string
  status: 'OK'
}

interface UnMatchInvoicesResponse {
  companyId: string
  status: 'OK'
}

interface SetTransactionVisibilityResponse {
  companyId: string
  status: 'OK'
}

interface SetTransactionsFilterPeriodResponse {
  companyId: string
  bankSettings: {
    transactionsFilterPeriod?: SelectionInterval
  }
  status: 'OK'
}

interface RawBankSettings {
  companyId: string
  transactionsFilterPeriod?: string
  settingType?: string
  created: string
  modified: string
  entity: string
}

type SetTransactionsFilterPeriodRawResponse = Omit<SetTransactionsFilterPeriodResponse, 'bankSettings'> & {
  bankSettings: RawBankSettings
}

interface GetBankTransactionSummary {
  company: number
  showBankingPopup: boolean
  status: bankConnectionStatus
  banks: Bank[]
}

type bankConnectionStatus = 'active' | 'disconnected' | 'expired' | 'incomplete' | 'noConnections'

export const toSelectionInterval = (value: string): SelectionInterval => {
  switch (value) {
    case '10':
      return SelectionInterval.TenDays
    case '30':
      return SelectionInterval.ThirtyDays
    case '60':
      return SelectionInterval.SixtyDays
    case '-1':
      return SelectionInterval.SixMonths
    default:
      return SelectionInterval.ThirtyDays
  }
}

export async function getEbBanksByCountry(country: string): Promise<BanksByCountryResponse> {
  const response = await axios.get<BanksByCountryResponse>(`/banking-api/eb/banks/${country}`, getRequestConfig())
  return response.data
}

export async function createEbBankingUrlByPsuType(request: BankingUrlRequest): Promise<BankingUrlResponse> {
  const response = await axios.post<BankingUrlResponse>('/banking-api/eb/banking-url', request, getRequestConfig())
  return response.data
}

export async function createEbBankingConsentByCode(body: BankingConsentReqBody): Promise<BankingConsentResponse> {
  const response = await axios.post<BankingConsentResponse>('/banking-api/eb/consent', body, getRequestConfig())
  return response.data
}

export async function removeAllActiveEbBankingConsent(): Promise<RemoveBankingConsentResponse> {
  const response = await axios.delete<RemoveBankingConsentResponse>('/banking-api/eb/consent', getRequestConfig())
  return response.data
}

export async function getEbBanksWithAccounts(): Promise<BanksWithAccountsResponse> {
  const response = await axios.get<BanksWithAccountsResponse>('/banking-api/eb/accounts', getRequestConfig())
  return response.data
}

export async function updateEbAccounts(body: AccountsUpdateReqBody): Promise<BanksWithAccountsResponse> {
  const response = await axios.patch<BanksWithAccountsResponse>('/banking-api/eb/accounts', body, getRequestConfig())
  return response.data
}

export async function getEbBankTransactions(datePeriod: PeriodPickerValue): Promise<BankTransactionsResponse> {
  const response = await axios.get<BankTransactionsResponse>(
    `/banking-api/eb/transactions?dateStart=${datePeriod.start}&dateEnd=${datePeriod.end}&transactionType=receivables&settingType=transactions`,
    getRequestConfig(),
  )
  return response.data
}

export async function matchEbInvoices(request: MatchInvoiceReqBody): Promise<MatchInvoicesResponse> {
  const url = `/banking-api/eb/transactions/${request.transactionId}/invoices/${request.invoiceId}`
  const response = await axios.put<MatchInvoicesResponse>(url, request.transactionInfo, getRequestConfig())
  return response.data
}

export async function unMatchEbInvoices(request: UnMatchInvoiceReqBody): Promise<UnMatchInvoicesResponse> {
  const url = `/banking-api/eb/transactions/${request.transactionId}/invoices/${request.invoiceId}`
  const response = await axios.delete<UnMatchInvoicesResponse>(url, getRequestConfig())
  return response.data
}

export async function setEbTransactionVisibility(
  request: TransactionViewOptionRequest,
): Promise<SetTransactionVisibilityResponse> {
  const url = `/banking-api/eb/transactions/${request.transactionId}`
  const response = await axios.put<SetTransactionVisibilityResponse>(
    url,
    { excluded: request.excluded },
    getRequestConfig(),
  )
  return response.data
}

export async function setTransactionsFilterPeriodApi(
  request: TransactionsFilterPeriodRequest,
): Promise<SetTransactionsFilterPeriodResponse> {
  const url = '/banking-api/eb/settings'
  try {
    const response = await axios.post<SetTransactionsFilterPeriodRawResponse>(
      url,
      {
        transactionsFilterPeriod: request.transactionsFilterPeriod,
        settingType: 'transactions',
      },
      getRequestConfig(),
    )
    const { companyId, bankSettings, status } = response.data
    return {
      companyId,
      bankSettings: {
        transactionsFilterPeriod: toSelectionInterval(bankSettings.transactionsFilterPeriod),
      },
      status,
    }
  } catch (error) {
    console.error(error)
    return error
  }
}

export async function getBankTransactionSummary(): Promise<GetBankTransactionSummary> {
  const url = '/banking-api/eb/status'
  const response = await axios.get<GetBankTransactionSummary>(url, getRequestConfig())
  return response.data
}
